import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPost, getChallenges } from '../lib/api';
import { hapticMedium, hapticError } from '../lib/haptics';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { toast } from 'sonner';
import { 
  ChevronLeft, Scale, Utensils, Dumbbell, Heart, Trophy,
  Image, Camera, X, MapPin, Globe, Users, Video, Play, Pause, Volume2, VolumeX, Plus
} from 'lucide-react';
import { takePhoto, pickFromGallery, pickMultipleFromGallery, isNative, compressVideo } from '../lib/native';
// Static import of Capacitor Camera for iOS — avoids dynamic `import()` lag
// inside the click handler, which would lose the WKWebView user-gesture
// token required by Camera.pickImages() / Camera.getPhoto() on iOS.
import { Camera as CapCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Filesystem } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';

// Max video duration in seconds
const MAX_VIDEO_DURATION = 10;

// Check if native iOS - don't use VideoRecorder component (causes crash)
const checkIsNativeIOS = () => {
  try {
    // Check if Capacitor is available and we're on iOS
    if (typeof window !== 'undefined' && window.Capacitor) {
      return window.Capacitor.getPlatform() === 'ios' && window.Capacitor.isNativePlatform();
    }
    return false;
  } catch (e) {
    return false;
  }
};

// Check if native Android — the media picker on Android requires an
// action sheet (Camera / Gallery) because the WebView's native file
// chooser cannot launch the camera intent reliably. iOS keeps its own
// action sheet provided by the file input.
const checkIsNativeAndroid = () => {
  try {
    if (typeof window !== 'undefined' && window.Capacitor) {
      return window.Capacitor.getPlatform() === 'android' && window.Capacitor.isNativePlatform();
    }
    return false;
  } catch (e) {
    return false;
  }
};

// Custom Video Preview Player - minimal controls (click to play/pause, mute button only)
function VideoPreviewPlayer({ src, onRemove, isDark }) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [thumbnail, setThumbnail] = useState(null);
  const [aspectRatio, setAspectRatio] = useState(9/16);
  const [isReady, setIsReady] = useState(false);

  // Generate thumbnail from first frame - with iOS safety
  useEffect(() => {
    if (!src || thumbnail) return;
    
    // For data URLs, try to generate thumbnail
    // For iOS, we'll just show the video element with poster
    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';
      
      const cleanup = () => {
        video.onloadedmetadata = null;
        video.onseeked = null;
        video.onerror = null;
        video.src = '';
        video.load();
      };
      
      video.onloadedmetadata = () => {
        try {
          // Get aspect ratio
          if (video.videoWidth && video.videoHeight) {
            const ratio = video.videoWidth / video.videoHeight;
            setAspectRatio(ratio);
          }
          video.currentTime = 0.1;
        } catch (e) {
          console.log('Video metadata error:', e);
          setIsReady(true);
          cleanup();
        }
      };
      
      video.onseeked = () => {
        try {
          if (video.videoWidth && video.videoHeight) {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0);
            setThumbnail(canvas.toDataURL('image/jpeg', 0.8));
          }
        } catch (e) {
          console.log('Thumbnail error:', e);
        }
        setIsReady(true);
        cleanup();
      };
      
      video.onerror = () => {
        console.log('Video load error');
        setIsReady(true);
        cleanup();
      };
      
      video.src = src;
    } catch (e) {
      console.log('Video setup error:', e);
      setIsReady(true);
    }
  }, [src, thumbnail]);

  const togglePlay = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    
    try {
      if (isPlaying) {
        video.pause();
        setIsPlaying(false);
      } else {
        video.play().then(() => setIsPlaying(true)).catch(e => {
          console.log('Play error:', e);
        });
      }
    } catch (e) {
      console.log('Toggle play error:', e);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    try {
      if (videoRef.current) {
        videoRef.current.muted = !videoRef.current.muted;
        setIsMuted(videoRef.current.muted);
      }
    } catch (e) {
      console.log('Mute error:', e);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    try {
      if (videoRef.current) videoRef.current.currentTime = 0;
    } catch (e) {
      console.log('Reset error:', e);
    }
  };

  // Determine container style based on aspect ratio
  const isVertical = aspectRatio < 1;
  // For vertical videos, set explicit width based on maxHeight to ensure proper centering
  const maxH = isVertical ? 400 : 250;
  const computedWidth = isVertical ? Math.round(maxH * aspectRatio) : undefined;
  const containerStyle = isVertical 
    ? { width: `${computedWidth}px`, height: `${maxH}px`, maxWidth: '100%' }
    : { aspectRatio: `${aspectRatio}`, maxHeight: `${maxH}px` };

  return (
    <div 
      className={`relative rounded-xl overflow-hidden border-2 ${isDark ? 'border-white/20' : 'border-gray-200'} bg-black`}
      style={containerStyle}
      onClick={togglePlay}
    >
      {/* Video element - no native controls */}
      <video
        ref={videoRef}
        src={src}
        muted={isMuted}
        playsInline
        onEnded={handleEnded}
        className="absolute inset-0 w-full h-full object-contain"
        style={{ opacity: isPlaying ? 1 : 0 }}
      />
      
      {/* Thumbnail - shown when paused */}
      {!isPlaying && thumbnail && (
        <img 
          src={thumbnail} 
          alt="Video preview" 
          className="absolute inset-0 w-full h-full object-contain"
        />
      )}
      
      {/* Loading if no thumbnail yet */}
      {!thumbnail && !isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}
      
      {/* Play/Pause overlay */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className={`w-14 h-14 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center transition-opacity ${isPlaying ? 'opacity-0' : 'opacity-100'}`}>
          <Play className="w-7 h-7 text-white ml-1" fill="white" />
        </div>
      </div>
      
      {/* Remove button */}
      <button 
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/70 flex items-center justify-center shadow-lg z-10"
      >
        <X className="w-5 h-5 text-white" />
      </button>
      
      {/* Mute button */}
      <button
        onClick={toggleMute}
        className="absolute top-3 left-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center z-10"
      >
        {isMuted ? (
          <VolumeX className="w-4 h-4 text-white" />
        ) : (
          <Volume2 className="w-4 h-4 text-white" />
        )}
      </button>
    </div>
  );
}

export default function NuovoPost() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  // Post types with translation keys
  const POST_TYPES = [
    { type: 'peso', icon: Scale, labelKey: 'newPost.weight', color: '#16A34A' },
    { type: 'pasto', icon: Utensils, labelKey: 'newPost.meal', color: '#F59E0B' },
    { type: 'esercizio', icon: Dumbbell, labelKey: 'newPost.exercise', color: '#3B82F6' },
    { type: 'motivazione', icon: Heart, labelKey: 'newPost.motivation', color: '#8B5CF6' },
    { type: 'traguardo', icon: Trophy, labelKey: 'newPost.milestone', color: '#E8334A' }
  ];
  
  const [postType, setPostType] = useState('motivazione');
  const [text, setText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  // Multi-image (gallery only). Holds the 2nd and 3rd images as objects
  // { base64, preview } so we can show them as thumbnails alongside
  // the primary `imagePreview`. Single-photo flow (camera, single
  // gallery pick) keeps the existing `imageUrl`/`imagePreview` path.
  const MAX_IMAGES = 3;
  const [extraImages, setExtraImages] = useState([]);  // Array of { base64, preview }
  // Active index of the composer carousel (0..totalSlides-1).
  const [composerPreviewIndex, setComposerPreviewIndex] = useState(0);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoPreview, setVideoPreview] = useState('');
  const [videoDuration, setVideoDuration] = useState(0);
  const [weightData, setWeightData] = useState({ weight: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [showMediaOptions, setShowMediaOptions] = useState(false);
  const [showVideoOptions, setShowVideoOptions] = useState(false);
  const videoInputRef = useRef(null);
  const videoFrontRef = useRef(null);  // Selfie camera
  const videoBackRef = useRef(null);   // Back camera
  const mediaInputRef = useRef(null);  // Unified media input (photo or video)
  
  // Visibility: 'public' or 'challenge'
  const [visibility, setVisibility] = useState('public');
  const [challenges, setChallenges] = useState([]);
  const [selectedChallengeId, setSelectedChallengeId] = useState(null);
  const [showChallengeSelector, setShowChallengeSelector] = useState(false);
  const [loadingChallenges, setLoadingChallenges] = useState(true);

  // Load user's challenges
  useEffect(() => {
    window.scrollTo(0, 0);
    const loadChallenges = async () => {
      try {
        const res = await getChallenges();
        const today = new Date().toISOString().split('T')[0];
        const activeChallenges = (res.data || []).filter(c => c.end_date >= today);
        setChallenges(activeChallenges);
        if (activeChallenges.length === 1) {
          setSelectedChallengeId(activeChallenges[0].challenge_id);
        }
      } catch (err) {
        console.error('Error loading challenges:', err);
      } finally {
        setLoadingChallenges(false);
      }
    };
    loadChallenges();
  }, []);

  // Pre-fill from URL params (e.g. from weight entry share button)
  useEffect(() => {
    const tipo = searchParams.get('tipo');
    const peso = searchParams.get('peso');
    if (tipo) {
      setPostType(tipo);
    }
    if (tipo === 'peso' && peso) {
      setWeightData({ weight: peso });
      const today = new Date();
      const dayName = today.toLocaleDateString(user?.language || 'it', { weekday: 'long', day: 'numeric', month: 'long' });
      setText(t('weightEntry.shareText', `Questo è il mio peso del giorno: ${dayName} - ${peso} kg`).replace('{{date}}', dayName).replace('{{weight}}', peso));
    }
  }, [searchParams, t, user?.language]);

  const selectedChallenge = challenges.find(c => c.challenge_id === selectedChallengeId);

  // Read EXIF orientation from image file
  const getExifOrientation = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const view = new DataView(e.target.result);
        if (view.getUint16(0, false) !== 0xFFD8) {
          resolve(1); // Not a JPEG
          return;
        }
        
        let offset = 2;
        while (offset < view.byteLength) {
          if (view.getUint16(offset, false) === 0xFFE1) {
            const exifOffset = offset + 4;
            if (view.getUint32(exifOffset, false) !== 0x45786966) {
              resolve(1);
              return;
            }
            
            const tiffOffset = exifOffset + 6;
            const littleEndian = view.getUint16(tiffOffset, false) === 0x4949;
            const ifdOffset = tiffOffset + view.getUint32(tiffOffset + 4, littleEndian);
            const entries = view.getUint16(ifdOffset, littleEndian);
            
            for (let i = 0; i < entries; i++) {
              const entryOffset = ifdOffset + 2 + i * 12;
              if (view.getUint16(entryOffset, littleEndian) === 0x0112) {
                resolve(view.getUint16(entryOffset + 8, littleEndian));
                return;
              }
            }
            resolve(1);
            return;
          }
          offset += 2 + view.getUint16(offset + 2, false);
        }
        resolve(1);
      };
      reader.readAsArrayBuffer(file.slice(0, 65536)); // Read first 64KB for EXIF
    });
  };

  // Process and correctly orient image using createImageBitmap for better browser support
  // This handles: EXIF orientation automatically via browser API
  const processImageFile = (file) => {
    return new Promise(async (resolve, reject) => {
      try {
        // Use createImageBitmap which respects EXIF orientation natively
        // This is supported in modern browsers and handles iOS camera captures correctly
        const options = { imageOrientation: 'from-image' };
        
        let bitmap;
        try {
          bitmap = await createImageBitmap(file, options);
        } catch (e) {
          // Fallback for browsers that don't support options
          bitmap = await createImageBitmap(file);
        }
        
        // Create canvas with correct dimensions
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Limit max dimension for compression
        const maxDimension = 1600;
        let width = bitmap.width;
        let height = bitmap.height;
        
        if (width > maxDimension || height > maxDimension) {
          const scale = maxDimension / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw the correctly oriented image
        ctx.drawImage(bitmap, 0, 0, width, height);
        bitmap.close(); // Free memory
        
        // Convert to JPEG with good quality
        const processedBase64 = canvas.toDataURL('image/jpeg', 0.85);
        resolve(processedBase64);
      } catch (error) {
        console.error('createImageBitmap failed, falling back to manual EXIF:', error);
        // Fallback to manual EXIF reading for older browsers
        try {
          const result = await processImageWithManualExif(file);
          resolve(result);
        } catch (e) {
          reject(e);
        }
      }
    });
  };

  // Fallback: Manual EXIF reading for browsers without createImageBitmap options support
  const processImageWithManualExif = (file) => {
    return new Promise(async (resolve) => {
      const exifOrientation = await getExifOrientation(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          let width = img.width;
          let height = img.height;
          
          // Determine if we need to apply EXIF-based rotation
          const needsRotation = exifOrientation >= 2 && exifOrientation <= 8;
          
          if (needsRotation) {
            // Set canvas dimensions based on EXIF orientation
            if (exifOrientation >= 5 && exifOrientation <= 8) {
              canvas.width = height;
              canvas.height = width;
            } else {
              canvas.width = width;
              canvas.height = height;
            }
            
            // Apply transforms based on EXIF orientation
            switch (exifOrientation) {
              case 2: ctx.transform(-1, 0, 0, 1, canvas.width, 0); break;
              case 3: ctx.transform(-1, 0, 0, -1, canvas.width, canvas.height); break;
              case 4: ctx.transform(1, 0, 0, -1, 0, canvas.height); break;
              case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;
              case 6: ctx.transform(0, 1, -1, 0, canvas.width, 0); break;
              case 7: ctx.transform(0, -1, -1, 0, canvas.width, canvas.height); break;
              case 8: ctx.transform(0, -1, 1, 0, 0, canvas.height); break;
              default: break;
            }
          } else {
            canvas.width = width;
            canvas.height = height;
          }
          
          ctx.drawImage(img, 0, 0);
          
          // Compress while maintaining quality
          const maxDimension = 1600;
          let finalCanvas = canvas;
          
          if (canvas.width > maxDimension || canvas.height > maxDimension) {
            const scale = maxDimension / Math.max(canvas.width, canvas.height);
            finalCanvas = document.createElement('canvas');
            finalCanvas.width = Math.round(canvas.width * scale);
            finalCanvas.height = Math.round(canvas.height * scale);
            const finalCtx = finalCanvas.getContext('2d');
            finalCtx.drawImage(canvas, 0, 0, finalCanvas.width, finalCanvas.height);
          }
          
          // Convert to JPEG with good quality
          const processedBase64 = finalCanvas.toDataURL('image/jpeg', 0.85);
          resolve(processedBase64);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle file select - process and correctly orient the image
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    
    // Show loading state while processing
    setImagePreview('loading');
    
    try {
      // Process image: handle EXIF orientation and compress
      const processedBase64 = await processImageFile(file);
      setImagePreview(processedBase64);
      setImageUrl(processedBase64); // Will be sent as image_base64
    } catch (err) {
      console.error('Image processing error:', err);
      // Fallback to reading without processing
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result;
        setImagePreview(base64);
        setImageUrl(base64);
      };
      reader.onerror = () => {
        setError('Errore nella lettura dell\'immagine');
        setImagePreview(null);
      };
      reader.readAsDataURL(file);
    }
    
    setShowImageOptions(false);
  };

  const handleSubmit = async () => {
    if (!text.trim()) {
      setError(t('newPost.writeSomething'));
      hapticError();
      return;
    }

    if (postType === 'peso' && !weightData.weight) {
      setError('Inserisci il peso');
      hapticError();
      return;
    }

    // Validate challenge selection
    if (visibility === 'challenge' && !selectedChallengeId) {
      setError('Seleziona una challenge');
      hapticError();
      return;
    }

    setError('');
    hapticMedium();

    // Multi-image branch — pack primary + extras into image_base64s.
    // The backend will process them in parallel and write back an
    // `image_urls` array, plus mirror image_urls[0] to image_url for
    // back-compat with the existing single-image rendering paths.
    const hasMultiImages = extraImages.length > 0 && imageUrl && imageUrl.startsWith('data:');
    const imageBase64s = hasMultiImages
      ? [imageUrl, ...extraImages.map((x) => x.base64)].slice(0, MAX_IMAGES)
      : null;

    const postData = {
      post_type: postType,
      text: text.trim(),
      image_base64: !hasMultiImages && imageUrl && imageUrl.startsWith('data:') ? imageUrl : null,
      image_base64s: imageBase64s,
      image_url: imageUrl && !imageUrl.startsWith('data:') ? imageUrl : null,
      video_base64: videoUrl && videoUrl.startsWith('data:') ? videoUrl : null,
      challenge_id: visibility === 'challenge' ? selectedChallengeId : null,
      data: postType === 'peso' ? { weight: parseFloat(weightData.weight) } : null
    };

    const hasVideo = !!(videoUrl && videoUrl.startsWith('data:'));
    const hasImage = !!(imageUrl && imageUrl.startsWith('data:')) || hasMultiImages;

    // If user selected "all challenges", convert to multi-challenge post (single record)
    if (visibility === 'challenge' && selectedChallengeId === '__all__') {
      if (challenges.length === 0) {
        setError('Non sei in nessuna challenge attiva');
        return;
      }
      postData.challenge_id = challenges[0].challenge_id;
      postData.challenge_ids = challenges.map(c => c.challenge_id);
    }

    // Navigate to feed immediately
    navigate('/app/feed', { replace: true });

    if (hasVideo) {
      toast.success(t('newPost.videoProcessing'), { duration: 3500 });
      window.dispatchEvent(new Event('video-processing-started'));
      const sessionToken = localStorage.getItem('session_token');
      fetch(`${API_URL}/api/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {})
        },
        body: JSON.stringify(postData)
      }).catch(err => {
        console.error('Video post upload error:', err);
      });
    } else if (hasImage) {
      toast.success(t('newPost.postPublished'), { duration: 2500 });
      const sessionToken = localStorage.getItem('session_token');
      fetch(`${API_URL}/api/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {})
        },
        body: JSON.stringify(postData)
      }).then(() => {
        window.dispatchEvent(new Event('feed-refresh'));
      }).catch(err => {
        console.error('Image post upload error:', err);
      });
    } else {
      // For text-only posts: quick, wait for confirm
      createPost(postData).then(() => {
        toast.success(t('newPost.postPublished'), { duration: 2500 });
        window.dispatchEvent(new Event('feed-refresh'));
      }).catch(err => {
        console.error('Post creation error:', err);
        toast.error(t('newPost.publishError'), { duration: 3000 });
      });
    }
  };

  // Handle video file selection
  const handleVideoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Verify it's a video file
    if (!file.type.startsWith('video/')) {
      setError(t('newPost.videoFileOnly'));
      return;
    }

    setError('');
    
    // Show loading state
    setVideoPreview('loading');
    
    try {
      // Read video and get duration (no longer blocking if > 10s)
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      const videoInfo = await new Promise((resolve, reject) => {
        video.onloadedmetadata = () => {
          resolve({
            duration: Math.round(video.duration),
            file: file
          });
        };
        video.onerror = () => reject(new Error('Formato video non supportato'));
        video.src = URL.createObjectURL(file);
      });
      
      // Check file size (max 100MB - backend will compress)
      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB > 100) {
        throw new Error(`Video troppo grande: ${sizeMB.toFixed(1)}MB (max 100MB)`);
      }
      
      // Read as data URL
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.onerror = () => reject(new Error('Errore lettura video'));
        reader.readAsDataURL(file);
      });
      
      setVideoPreview(dataUrl);
      setVideoUrl(dataUrl);
      
      // Show actual duration, but note it will be trimmed
      if (videoInfo.duration > MAX_VIDEO_DURATION) {
        setVideoDuration(MAX_VIDEO_DURATION);
        // Show info toast that video will be trimmed
        setError(`Il video (${videoInfo.duration}s) verrà tagliato a ${MAX_VIDEO_DURATION}s`);
      } else {
        setVideoDuration(videoInfo.duration);
      }
      
      // Clear image(s) if video is selected — video XOR images.
      setImagePreview('');
      setImageUrl('');
      setExtraImages([]);
      
    } catch (err) {
      console.error('Video processing error:', err);
      setError(err.message || 'Errore nel caricamento del video');
      setVideoPreview('');
    }
    
    setShowMediaOptions(false);
  };

  // Handle unified media selection (photo or video).
  // Supports MULTI-IMAGE pick from gallery (up to MAX_IMAGES). When the
  // user picks a video — even alongside images — we honour the
  // "video XOR images" rule and process only the first video. When the
  // user picks images, the first goes into the primary preview slot
  // and the rest into `extraImages` (max MAX_IMAGES - 1 extras).
  const handleMediaSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Find any video among the selection.
    const firstVideo = files.find((f) => f.type.startsWith('video/'));
    if (firstVideo) {
      // Mimic the existing single-video path. Strip everything else.
      const fakeEvent = { target: { files: [firstVideo] } };
      await handleVideoSelect(fakeEvent);
      // Reset the input so re-selecting the same file later still fires onChange.
      try { e.target.value = ''; } catch (_) { /* noop */ }
      return;
    }

    // Image-only selection: respect the MAX_IMAGES cap and skip extras.
    const imageFiles = files
      .filter((f) => f.type.startsWith('image/'))
      .slice(0, MAX_IMAGES);

    if (imageFiles.length === 0) {
      setError('Formato non supportato. Seleziona una foto o un video.');
      try { e.target.value = ''; } catch (_) { /* noop */ }
      return;
    }

    setError('');
    // Single-image fast path — keep the existing single-image behaviour.
    if (imageFiles.length === 1) {
      const fakeEvent = { target: { files: [imageFiles[0]] } };
      await handleFileSelect(fakeEvent);
      try { e.target.value = ''; } catch (_) { /* noop */ }
      return;
    }

    // Multi-image: process the first as the primary preview, rest as extras.
    setImagePreview('loading');
    try {
      const [first, ...rest] = imageFiles;
      const firstB64 = await processImageFile(first);
      setImagePreview(firstB64);
      setImageUrl(firstB64);

      const extras = [];
      for (const f of rest) {
        try {
          const b64 = await processImageFile(f);
          extras.push({ base64: b64, preview: b64 });
        } catch (err) {
          console.warn('[NuovoPost] skipping image (process failed):', err);
        }
      }
      setExtraImages(extras);
      setComposerPreviewIndex(0);
    } catch (err) {
      console.error('Multi-image processing error:', err);
      setError(t('newPost.imageError', "Errore nella lettura dell'immagine"));
      setImagePreview('');
      setExtraImages([]);
    }
    try { e.target.value = ''; } catch (_) { /* noop */ }
  };

  // Remove a specific image from the multi-image preview (1-based slot).
  // Slot 0 = primary (imagePreview); slots 1..N = extraImages[idx].
  const removeImageAt = (slot) => {
    if (slot === 0) {
      // If user removes the primary while extras exist, promote the next one.
      if (extraImages.length > 0) {
        const [next, ...rest] = extraImages;
        setImagePreview(next.preview);
        setImageUrl(next.base64);
        setExtraImages(rest);
      } else {
        setImagePreview('');
        setImageUrl('');
      }
    } else {
      setExtraImages((prev) => prev.filter((_, i) => i !== slot - 1));
    }
  };

  return (
    <div
      className={`page-enter fixed inset-0 flex flex-col overflow-hidden ${isDark ? 'bg-[#080D1A]' : 'bg-white'}`}
      style={{ height: '100dvh' }}
      data-testid="nuovo-post-page"
    >
      {/* Fixed Header - with safe area */}
      <div 
        className={`flex-shrink-0 px-4 py-3 flex items-center gap-3 border-b ${isDark ? 'bg-[#080D1A] border-white/10' : 'bg-white border-gray-200'} shadow-sm`}
        style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 0px))' }}
      >
        <div className="max-w-md mx-auto w-full flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}
          >
            <ChevronLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
          </button>
          <h1 className={`font-display text-xl flex-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('newPost.title')}</h1>
          <button
            onClick={handleSubmit}
            disabled={saving || !text.trim()}
            className="btn-primary px-6 py-2.5"
            data-testid="publish-post-btn"
          >
            {saving ? t('newPost.publishing') : t('newPost.publish')}
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto overscroll-contain pb-24">
        {/* User Row */}
        <div className={`px-4 py-3 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
          {/* User info */}
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-[#E8334A]/20 flex items-center justify-center font-medium text-[#E8334A]">
                {(user?.username || user?.name)?.[0]?.toUpperCase() || '?'}
              </div>
            )}
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {user?.username || user?.name}
            </span>
          </div>
        
          {/* Visibility buttons - on new line */}
          <div className="flex gap-2">
            <button
              onClick={() => setVisibility('public')}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                visibility === 'public'
                  ? 'bg-[#1d3557] text-white'
                  : isDark ? 'bg-white/5 text-white/60' : 'bg-gray-100 text-gray-500'
              }`}
            >
              <Globe className="w-4 h-4" />
              {t('newPost.public')}
            </button>
            {!loadingChallenges && challenges.length > 0 && (
              <button
                onClick={() => {
                  setVisibility('challenge');
                  if (challenges.length > 1) setShowChallengeSelector(true);
                }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  visibility === 'challenge'
                    ? 'bg-[#E8334A] text-white'
                    : isDark ? 'bg-white/5 text-white/60' : 'bg-gray-100 text-gray-500'
                }`}
              >
                <Users className="w-4 h-4" />
                {t('newPost.challenge')}
              </button>
            )}
          </div>
        </div>

      {/* Post Type Grid */}
      <div className={`px-4 py-3 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
        <div className="grid grid-cols-5 gap-2">
          {POST_TYPES.map(type => {
            const Icon = type.icon;
            const isSelected = postType === type.type;
            return (
              <button
                key={type.type}
                onClick={() => setPostType(type.type)}
                className={`flex flex-col items-center gap-1 py-2 rounded-xl font-medium transition-all ${
                  isSelected 
                    ? 'text-white' 
                    : isDark ? 'bg-white/5 text-white/60' : 'bg-gray-100 text-gray-600'
                }`}
                style={isSelected ? { backgroundColor: type.color } : {}}
              >
                <Icon className="w-4 h-4" />
                <span className="text-xs">{t(type.labelKey)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Weight input for peso type */}
      {postType === 'peso' && (
        <div className="px-4 py-3">
          <label className={`text-xs font-medium mb-2 block ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            {t('weightEntry.currentWeight')} (kg)
          </label>
          <div className={`flex items-center gap-3 p-4 rounded-xl border-2 ${
            isDark ? 'bg-white/10 border-white/30' : 'bg-gray-50 border-gray-300'
          }`}>
            <Scale className={`w-5 h-5 ${isDark ? 'text-[#E8334A]' : 'text-[#E8334A]'}`} />
            <input
              type="number"
              step="0.1"
              value={weightData.weight}
              onChange={e => setWeightData({ weight: e.target.value })}
              placeholder="Es. 75.5"
              className={`flex-1 bg-transparent text-xl font-display outline-none ${isDark ? 'text-white placeholder:text-white/50' : 'text-gray-900 placeholder:text-gray-400'}`}
              data-testid="post-weight-input"
            />
            <span className={`text-lg font-medium ${isDark ? 'text-white/40' : 'text-gray-400'}`}>kg</span>
          </div>
        </div>
      )}

      {/* Text area - Well defined with visible borders */}
      <div className="px-4 py-3">
        <label className={`text-xs font-medium mb-2 block ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
          {postType === 'peso' ? t('weightEntry.note') : t('newPost.whatShare')}
        </label>
        <div className={`p-4 rounded-xl border-2 ${
          isDark ? 'bg-white/10 border-white/30' : 'bg-gray-50 border-gray-300'
        }`}>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={getPlaceholder(postType, t)}
            rows={4}
            className={`w-full bg-transparent text-base resize-none outline-none ${
              isDark ? 'text-white placeholder:text-white/50' : 'text-gray-900 placeholder:text-gray-400'
            }`}
            data-testid="post-text-input"
            autoFocus
          />
        </div>
      </div>

      {/* Image Preview — single horizontal swipe carousel (matches the
          feed rendering). All photos including the "add-more" slot are
          rendered as siblings that snap into place; the user swipes
          between them. Dots + a 1/N counter chip mirror the in-feed
          PostImage component for visual consistency. */}
      {imagePreview && (
        <div className="px-4 pb-3 flex flex-col items-center gap-2" data-testid="composer-image-preview">
          <div className={`relative rounded-xl overflow-hidden border-2 ${isDark ? 'border-white/20' : 'border-gray-200'} max-w-sm w-full`}>
            {imagePreview === 'loading' ? (
              <div className="w-full h-64 flex items-center justify-center bg-black/20">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-8 h-8 border-3 border-[#E8334A] border-t-transparent rounded-full animate-spin" />
                  <span className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-500'}`}>{t('common.loading')}</span>
                </div>
              </div>
            ) : (() => {
              // Build the array of slides — selected photos first, then
              // an optional trailing "+" slide if we're below the cap.
              const photos = [{ src: imagePreview }, ...extraImages.map((x) => ({ src: x.preview }))];
              const showAddSlot = photos.length < MAX_IMAGES;
              const totalSlides = photos.length + (showAddSlot ? 1 : 0);
              return (
                <>
                  <div
                    onScroll={(e) => {
                      const el = e.currentTarget;
                      const w = el.clientWidth;
                      if (!w) return;
                      const i = Math.max(0, Math.min(totalSlides - 1, Math.round(el.scrollLeft / w)));
                      if (i !== composerPreviewIndex) setComposerPreviewIndex(i);
                    }}
                    className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar"
                    style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
                    data-testid="composer-carousel"
                  >
                    {photos.map((p, idx) => (
                      <div
                        key={`p-${idx}`}
                        className="relative flex-shrink-0 w-full snap-start"
                        style={{ scrollSnapAlign: 'start' }}
                        data-testid={`composer-slide-${idx}`}
                      >
                        <img src={p.src} alt={`Foto ${idx + 1}`} className="w-full max-h-80 object-contain bg-black/5" />
                        <button
                          onClick={() => removeImageAt(idx)}
                          data-testid={`remove-image-${idx}`}
                          className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/70 flex items-center justify-center shadow-lg z-10"
                          aria-label={t('common.remove', 'Rimuovi')}
                        >
                          <X className="w-5 h-5 text-white" />
                        </button>
                      </div>
                    ))}
                    {showAddSlot && (
                      <button
                        type="button"
                        onClick={() => mediaInputRef.current?.click()}
                        data-testid="add-more-images-btn"
                        className={`flex-shrink-0 w-full snap-start flex items-center justify-center max-h-80 h-64 ${
                          isDark ? 'bg-white/[0.03]' : 'bg-gray-50'
                        }`}
                        style={{ scrollSnapAlign: 'start' }}
                      >
                        <div className={`flex flex-col items-center gap-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                          <Plus className="w-8 h-8" />
                          <span className="text-xs font-medium">
                            {t('newPost.addMore', "Aggiungi foto")}
                          </span>
                        </div>
                      </button>
                    )}
                  </div>

                  {/* Counter chip — only meaningful when ≥2 actual photos */}
                  {photos.length > 1 && (
                    <span
                      className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[11px] font-medium pointer-events-none z-10"
                      data-testid="multi-image-counter"
                    >
                      {Math.min(composerPreviewIndex + 1, photos.length)}/{photos.length}
                    </span>
                  )}

                  {/* Dot indicators — render only when more than one slide */}
                  {totalSlides > 1 && (
                    <div
                      className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 pointer-events-none"
                      data-testid="composer-dots"
                    >
                      {Array.from({ length: totalSlides }).map((_, i) => {
                        const isAddSlot = showAddSlot && i === totalSlides - 1;
                        const active = i === composerPreviewIndex;
                        return (
                          <span
                            key={i}
                            className={`rounded-full transition-all duration-200 ${
                              active
                                ? 'w-2 h-2 bg-white'
                                : `w-1.5 h-1.5 ${isAddSlot ? 'bg-white/30' : 'bg-white/50'}`
                            }`}
                          />
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Video Preview - centered */}
      {videoPreview && (
        <div className="px-4 pb-3 flex-1 flex flex-col items-center justify-center">
          <label className={`text-xs font-medium mb-2 block text-center ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            {t('newPost.videoLabel').replace('{sec}', videoDuration).replace('{max}', MAX_VIDEO_DURATION)}
          </label>
          <div className="w-full flex justify-center">
            {videoPreview === 'loading' ? (
              <div className={`w-full max-w-sm h-64 flex items-center justify-center rounded-xl border-2 ${isDark ? 'border-white/20 bg-black/20' : 'border-gray-200 bg-gray-100'}`}>
                <div className="flex flex-col items-center gap-2">
                  <div className="w-8 h-8 border-3 border-[#E8334A] border-t-transparent rounded-full animate-spin" />
                  <span className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-500'}`}>{t('newPost.videoProcessingLabel', 'Elaborazione video...')}</span>
                </div>
              </div>
            ) : (
              <div className="max-w-sm w-full mx-auto flex justify-center">
                <div className="w-fit mx-auto">
                  <VideoPreviewPlayer 
                  src={videoPreview}
                  isDark={isDark}
                  onRemove={() => { 
                    setVideoPreview(''); 
                    setVideoUrl(''); 
                    setVideoDuration(0); 
                  }}
                />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Media Buttons */}
      <div className={`px-4 py-6 ${isDark ? '' : ''}`}>
        {/* Single media button — opens the iOS/Android native file
            picker DIRECTLY, exactly like the profile-photo upload in
            Profilo.js does. iOS shows its own action sheet ("Photo
            Library / Take Photo / Choose File"). The input has
            `multiple` so users can still select up to 3 photos in one
            sweep, and `accept="image/*,video/*"` lets them attach a
            video as alternative. */}
        {!videoPreview && !imagePreview && (
          <button 
            onClick={() => {
              // Android E iOS nativi: bottom sheet React custom.
              // Web: file input HTML (mediaInputRef apre il file picker browser).
              if (checkIsNativeAndroid() || checkIsNativeIOS()) {
                setShowImageOptions(true);
              } else {
                mediaInputRef.current?.click();
              }
            }}
            data-testid="open-media-picker-btn"
            className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl text-base font-semibold bg-gradient-to-r from-[#E8334A] to-[#C41E3A] text-white shadow-lg active:scale-[0.98] transition-transform"
          >
            <Image className="w-6 h-6" />
            {t('newPost.photoOrVideo')}
          </button>
        )}
        
        {postType === 'esercizio' && !videoPreview && !imagePreview && (
          <button 
            onClick={() => navigate('/app/camminata')}
            className="w-full mt-3 flex items-center justify-center gap-3 px-6 py-4 rounded-2xl text-base font-semibold bg-[#14B8A6] text-white shadow-lg active:scale-[0.98] transition-transform"
          >
            <MapPin className="w-6 h-6" />
            {t('walk.title')} GPS
          </button>
        )}
        
        {error && <p className="text-[#E8334A] text-sm mt-3 text-center">{error}</p>}
      </div>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={videoInputRef}
        type="file"
        onChange={handleVideoSelect}
        className="hidden"
      />
      {/* Unified media input - accepts both photos and videos.
          The `multiple` attribute lets users pick up to MAX_IMAGES
          photos in one gallery sweep. If a video is in the selection
          we still respect "video XOR images" and take only the first
          video (see handleMediaSelect). */}
      <input
        ref={mediaInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        onChange={handleMediaSelect}
        className="hidden"
      />
      {/* Video camera inputs - front (selfie) and back */}
      <input
        ref={videoFrontRef}
        type="file"
        accept="video/*"
        capture="user"
        onChange={handleVideoSelect}
        className="hidden"
      />
      <input
        ref={videoBackRef}
        type="file"
        accept="video/*"
        capture="environment"
        onChange={handleVideoSelect}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Video Options Modal */}
      {showVideoOptions && (
        <div className="action-sheet" onClick={() => setShowVideoOptions(false)}>
          <div 
            className="action-sheet-content animate-slide-up"
            onClick={e => e.stopPropagation()}
          >
            <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
              <h3 className={`font-display text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Registra video (max {MAX_VIDEO_DURATION}s)
              </h3>
            </div>
            
            {/* Record with front camera (selfie) */}
            <button 
              onClick={() => {
                setShowVideoOptions(false);
                videoFrontRef.current?.click();
              }}
              className="action-sheet-item"
            >
              <Camera className="w-5 h-5" />
              {t('newPost.selfieCamera')}
            </button>
            
            {/* Record with back camera */}
            <button 
              onClick={() => {
                setShowVideoOptions(false);
                videoBackRef.current?.click();
              }}
              className="action-sheet-item"
            >
              <Video className="w-5 h-5" />
              {t('newPost.backCamera')}
            </button>
            
            {/* Pick from gallery */}
            <button 
              onClick={() => {
                videoInputRef.current?.click();
                setShowVideoOptions(false);
              }}
              className="action-sheet-item"
            >
              <Image className="w-5 h-5" />
              {t('newPost.pickFromGallery')}
            </button>
            
            <button 
              onClick={() => setShowVideoOptions(false)}
              className="action-sheet-item destructive"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Image Options Bottom Sheet — Android premium design */}
      {showImageOptions && (
        <div 
          className="fixed inset-0 z-[200] flex items-end justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={() => setShowImageOptions(false)}
          data-testid="image-options-sheet"
        >
          <div 
            className={`w-full max-w-md rounded-t-3xl shadow-2xl animate-slide-up overflow-hidden ${
              isDark ? 'bg-[#1a1a1a]' : 'bg-white'
            }`}
            onClick={e => e.stopPropagation()}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className={`w-12 h-1.5 rounded-full ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
            </div>
            
            {/* Header */}
            <div className="px-6 pt-3 pb-5">
              <h3 className={`font-display text-xl text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('newPost.addPhoto') || 'Aggiungi foto'}
              </h3>
              <p className={`text-sm text-center mt-1 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {t('newPost.chooseSource') || 'Scegli da dove caricare la foto'}
              </p>
            </div>
            
            {/* Option cards */}
            <div className="px-5 pb-3 grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="modal-take-photo-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowImageOptions(false);
                  if (checkIsNativeIOS()) {
                    // iOS: Camera.getPhoto chiamato SINCRONAMENTE nel
                    // click handler. Modulo importato staticamente per
                    // preservare il user-gesture token di WKWebView.
                    CapCamera.getPhoto({
                      quality: 85,
                      allowEditing: false,
                      resultType: CameraResultType.DataUrl,
                      source: CameraSource.Camera,
                      width: 1200,
                      height: 1200,
                      correctOrientation: true,
                    }).then((photo) => {
                      if (photo && photo.dataUrl) {
                        setImagePreview(photo.dataUrl);
                        setImageUrl(photo.dataUrl);
                        setExtraImages([]);
                      }
                    }).catch((err) => {
                      console.warn('[NuovoPost] iOS getPhoto error:', err?.message || err);
                      if (err?.message && !/cancel/i.test(err.message)) {
                        toast.error(`Camera: ${err.message}`);
                      }
                    });
                  } else {
                    setTimeout(() => cameraInputRef.current?.click(), 50);
                  }
                }}
                className="group relative flex flex-col items-center gap-3 p-5 rounded-2xl bg-gradient-to-br from-[#E8334A] to-[#C41E3A] text-white shadow-lg active:scale-[0.96] transition-all"
              >
                <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Camera className="w-7 h-7" strokeWidth={2.2} />
                </div>
                <span className="font-semibold text-sm">
                  {t('newPost.takePhoto') || 'Scatta foto'}
                </span>
              </button>
              
              <button
                type="button"
                data-testid="modal-pick-gallery-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowImageOptions(false);
                  if (checkIsNativeIOS()) {
                    // iOS: Camera.getPhoto con source=Photos apre la
                    // photo library nativa e ritorna DIRETTAMENTE il
                    // dataUrl. Singola foto: per multi-selezione il
                    // pickImages plugin v7 fallisce con "Error loading
                    // image" su HEIC/iCloud. L'utente aggiunge altre
                    // foto via "+ Aggiungi altra" (fino a MAX_IMAGES).
                    CapCamera.getPhoto({
                      quality: 85,
                      allowEditing: false,
                      resultType: CameraResultType.DataUrl,
                      source: CameraSource.Photos,
                      width: 1200,
                      height: 1200,
                      correctOrientation: true,
                    }).then((photo) => {
                      if (photo && photo.dataUrl) {
                        setImagePreview(photo.dataUrl);
                        setImageUrl(photo.dataUrl);
                        setExtraImages([]);
                      }
                    }).catch((err) => {
                      console.warn('[NuovoPost] iOS gallery getPhoto error:', err?.message || err);
                      if (err?.message && !/cancel/i.test(err.message)) {
                        toast.error(`Galleria: ${err.message}`);
                      }
                    });
                  } else {
                    setTimeout(() => fileInputRef.current?.click(), 50);
                  }
                }}
                className={`group relative flex flex-col items-center gap-3 p-5 rounded-2xl shadow-lg active:scale-[0.96] transition-all ${
                  isDark 
                    ? 'bg-gradient-to-br from-purple-600 to-indigo-700 text-white' 
                    : 'bg-gradient-to-br from-purple-500 to-indigo-600 text-white'
                }`}
              >
                <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Image className="w-7 h-7" strokeWidth={2.2} />
                </div>
                <span className="font-semibold text-sm">
                  {t('newPost.pickFromGallery') || 'Galleria'}
                </span>
              </button>
            </div>
            
            {/* Cancel pill button */}
            <div className="px-5 pt-2 pb-6">
              <button 
                type="button"
                onClick={() => setShowImageOptions(false)}
                className={`w-full py-3.5 rounded-2xl font-semibold text-base transition-colors ${
                  isDark 
                    ? 'bg-white/10 text-white hover:bg-white/15' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('common.cancel') || 'Annulla'}
              </button>
            </div>
            
            {/* Safe area bottom inset */}
            <div className="pb-[env(safe-area-inset-bottom)]" />
          </div>
        </div>
      )}

      {/* Challenge Selector Modal */}
      {showChallengeSelector && challenges.length > 1 && (
        <div 
          className="fixed inset-0 flex items-end justify-center bg-black/60 backdrop-blur-sm"
          style={{ zIndex: 9999 }}
          onClick={() => setShowChallengeSelector(false)}
        >
          <div 
            className={`w-full max-w-md rounded-t-3xl p-6 animate-slide-up ${
              isDark ? 'bg-[#0D1424]' : 'bg-white'
            }`}
            style={{ paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))' }}
            onClick={e => e.stopPropagation()}
          >
            <div className={`w-12 h-1 rounded-full mx-auto mb-6 ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
            
            <h2 className={`font-display text-xl mb-6 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('newPost.selectChallenge')}
            </h2>

            <div className="space-y-3 mb-6">
              {challenges.length > 1 && (
                <button
                  onClick={() => {
                    setSelectedChallengeId('__all__');
                    setShowChallengeSelector(false);
                  }}
                  data-testid="challenge-option-all"
                  className={`w-full p-4 rounded-xl flex items-center gap-3 transition-all border-2 border-dashed ${
                    selectedChallengeId === '__all__'
                      ? 'bg-[#E8334A] text-white border-[#E8334A]'
                      : isDark
                        ? 'bg-white/5 text-white border-[#E8334A]/40 hover:bg-white/10'
                        : 'bg-gray-100 text-gray-900 border-[#E8334A]/40 hover:bg-gray-200'
                  }`}
                >
                  <span className="text-2xl">🌐</span>
                  <div className="flex-1 text-left">
                    <p className="font-medium">{t('newPost.allChallenges', 'Tutte le sfide')}</p>
                    <p className={`text-sm ${
                      selectedChallengeId === '__all__' ? 'text-white/70' : isDark ? 'text-white/50' : 'text-gray-500'
                    }`}>
                      {t('newPost.publishToAll', 'Pubblica su tutte le sfide attive ({n})').replace('{n}', challenges.length)}
                    </p>
                  </div>
                </button>
              )}
              {challenges.map(challenge => (
                <button
                  key={challenge.challenge_id}
                  onClick={() => {
                    setSelectedChallengeId(challenge.challenge_id);
                    setShowChallengeSelector(false);
                  }}
                  className={`w-full p-4 rounded-xl flex items-center gap-3 transition-all ${
                    selectedChallengeId === challenge.challenge_id
                      ? 'bg-[#E8334A] text-white'
                      : isDark ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  <span className="text-2xl">{challenge.emoji}</span>
                  <div className="flex-1 text-left">
                    <p className="font-medium">{challenge.name}</p>
                    <p className={`text-sm ${
                      selectedChallengeId === challenge.challenge_id ? 'text-white/70' : isDark ? 'text-white/50' : 'text-gray-500'
                    }`}>
                      {t('newPost.participantsCount').replace('{n}', challenge.participants?.length || 0)}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            <button 
              onClick={() => setShowChallengeSelector(false)}
              className="btn-secondary w-full"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function getPlaceholder(type, t) {
  const keyMap = {
    peso: 'placeholderPeso',
    pasto: 'placeholderPasto',
    esercizio: 'placeholderEsercizio',
    motivazione: 'placeholderMotivazione',
    traguardo: 'placeholderTraguardo',
  };
  const key = keyMap[type] || 'placeholderDefault';
  return t ? t(`newPost.${key}`) : '';
}
